import { useEffect, useRef, useState } from 'react';

export interface LookupOption {
  value: string;
  label: string;
}

interface LookupSelectProps {
  label?: string;
  placeholder?: string;
  value: string;
  displayValue?: string;
  onChange: (value: string, option?: LookupOption) => void;
  fetchOptions: (query: string) => Promise<LookupOption[]>;
  error?: string;
  required?: boolean;
}

/**
 * Searchable async dropdown — replaces raw free-text foreign-key inputs
 * (e.g. typing a department UUID by hand) across the app.
 */
export default function LookupSelect({
  label,
  placeholder = 'Search...',
  value,
  displayValue,
  onChange,
  fetchOptions,
  error,
  required,
}: LookupSelectProps) {
  const [query, setQuery] = useState(displayValue || '');
  const [options, setOptions] = useState<LookupOption[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    setQuery(displayValue || '');
  }, [displayValue]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const runSearch = (text: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const results = await fetchOptions(text);
        setOptions(results);
      } catch {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 250);
  };

  const handleFocus = () => {
    setIsOpen(true);
    if (options.length === 0) runSearch(query);
  };

  const handleInputChange = (text: string) => {
    setQuery(text);
    if (!text) onChange('', undefined);
    runSearch(text);
  };

  const handleSelect = (opt: LookupOption) => {
    setQuery(opt.label);
    onChange(opt.value, opt);
    setIsOpen(false);
  };

  return (
    <div className="flex flex-col gap-1 relative" ref={containerRef}>
      {label && (
        <label className="text-sm font-medium text-gray-700">
          {label}
          {required && <span className="text-red-500"> *</span>}
        </label>
      )}
      <input
        type="text"
        value={query}
        onChange={(e) => handleInputChange(e.target.value)}
        onFocus={handleFocus}
        placeholder={placeholder}
        className={`px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
          error ? 'border-red-400' : 'border-gray-300'
        }`}
        autoComplete="off"
      />
      <input type="hidden" value={value} required={required} />
      {isOpen && (
        <div className="absolute top-full mt-1 left-0 right-0 z-20 bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
          {loading && <div className="px-4 py-2 text-sm text-gray-500">Searching...</div>}
          {!loading && options.length === 0 && (
            <div className="px-4 py-2 text-sm text-gray-500">No results</div>
          )}
          {!loading &&
            options.map((opt) => (
              <button
                type="button"
                key={opt.value}
                onClick={() => handleSelect(opt)}
                className="w-full text-left px-4 py-2 text-sm hover:bg-blue-50"
              >
                {opt.label}
              </button>
            ))}
        </div>
      )}
      {error && <span className="text-xs text-red-600">{error}</span>}
    </div>
  );
}
